import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCountries,
  fetchLocation,
  fetchLocationChildren,
  fetchMakes,
  fetchPopularLocations,
  fetchResolvedLocation,
  fetchSubcategories,
  selectCountries,
  selectLocation,
  selectLocationChildren,
  selectLocations,
  selectMakes,
  selectPopularLocations,
  selectResolvedLocation,
  selectResolvedLocations,
  selectSubcategories
} from "./configuration.ducks";
import { rootReducerInterface } from "../../typings/interfaces";
import { isCountryFilterId } from "../../models/enums";

// Each hook returns the dataset for the given scope and triggers the fetch the
// first time it is needed. `undefined` means "not loaded yet".

export const useMakes = (category: string) => {
  const dispatch = useDispatch();
  const makes = useSelector((state: rootReducerInterface) => selectMakes(state, category));

  useEffect(() => {
    if (category && makes === undefined) {
      dispatch(fetchMakes(category));
    }
  }, [category, makes === undefined]);

  return makes;
};

export const useSubcategories = (module: string, category: string) => {
  const dispatch = useDispatch();
  const subcategories = useSelector((state: rootReducerInterface) =>
    selectSubcategories(state, module, category)
  );

  useEffect(() => {
    if (module && category && subcategories === undefined) {
      dispatch(fetchSubcategories(module, category));
    }
  }, [module, category, subcategories === undefined]);

  return subcategories;
};

// `enabled` lets a component that only sometimes needs the list (e.g. a picker
// that is a make or subcategory picker most of the time) skip the request.
export const useCountries = (enabled: boolean = true) => {
  const dispatch = useDispatch();
  const countries = useSelector(selectCountries);

  useEffect(() => {
    if (enabled && countries === null) {
      dispatch(fetchCountries());
    }
  }, [enabled, countries === null]);

  return countries;
};

export const usePopularLocations = () => {
  const dispatch = useDispatch();
  const popularLocations = useSelector(selectPopularLocations);

  useEffect(() => {
    if (popularLocations === null) {
      dispatch(fetchPopularLocations());
    }
  }, [popularLocations === null]);

  return popularLocations;
};

export const useLocationChildren = (parentId: string | null | undefined) => {
  const dispatch = useDispatch();
  const children = useSelector((state: rootReducerInterface) =>
    selectLocationChildren(state, parentId)
  );

  useEffect(() => {
    if (parentId && children === undefined) {
      dispatch(fetchLocationChildren(parentId));
    }
  }, [parentId, children === undefined]);

  return children;
};

// Single location by id (configuration/locations/:id). The two country filter
// ids are never fetched - they are not locations.
export const useLocation = (id: string | null | undefined) => {
  const dispatch = useDispatch();
  const location = useSelector((state: rootReducerInterface) => selectLocation(state, id));

  useEffect(() => {
    if (id && !isCountryFilterId(id) && location === undefined) {
      dispatch(fetchLocation(id));
    }
  }, [id, location === undefined]);

  return location;
};

// Several locations by id; returns the whole id -> location map.
export const useLocations = (ids: string[]) => {
  const dispatch = useDispatch();
  const locations = useSelector(selectLocations);
  const idsKey = (ids || []).join(",");

  useEffect(() => {
    (ids || []).forEach(id => {
      if (id && !isCountryFilterId(id) && locations[id] === undefined) {
        dispatch(fetchLocation(id));
      }
    });
  }, [idsKey, locations]);

  return locations;
};

// Resolved location (ancestor names and routes) by id.
export const useResolvedLocation = (id: string | null | undefined) => {
  const dispatch = useDispatch();
  const location = useSelector((state: rootReducerInterface) =>
    selectResolvedLocation(state, id)
  );

  useEffect(() => {
    if (id && !isCountryFilterId(id) && location === undefined) {
      dispatch(fetchResolvedLocation(id));
    }
  }, [id, location === undefined]);

  return location;
};

export const useResolvedLocations = (ids: string[]) => {
  const dispatch = useDispatch();
  const locations = useSelector(selectResolvedLocations);
  const idsKey = (ids || []).join(",");

  useEffect(() => {
    (ids || []).forEach(id => {
      if (id && !isCountryFilterId(id) && locations[id] === undefined) {
        dispatch(fetchResolvedLocation(id));
      }
    });
  }, [idsKey, locations]);

  return useMemo(() => locations, [locations]);
};



// WEBPACK FOOTER //
// ./src/routes/Configuration/hooks.ts